import {
  faUsers,
  faWallet,
  faQrcode,
  faCreditCard,
  faBox,
  faGift,
  faHistory,
  faUndoAlt,
  faClipboardList,
  faArrowsAlt,
  faChartBar,
  faImage,
  faUserCheck,
  faHandshake,
  faTruckFast,
  faMoneyBill,
  faIdCard,
  faCalendarCheck,
  faAddressCard
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export interface ReportCard {
  id: string;
  name: string;
  title: string;
  description: string;
  icon: IconDefinition;
  endpoint: string;
  category: string;
  /** resourceName from the user-access-resources API (without ':read' suffix) */
  permissionKey?: string;
}

export const REPORT_CARDS: ReportCard[] = [
  // Users Category
  {
    id: 'users',
    name: 'Users',
    title: 'Users Report',
    description: 'View all registered users and their activity data.',
    icon: faUsers,
    endpoint: 'Users',
    category: 'Users',
    permissionKey: 'users-sheet'
  },
  {
    id: 'users-data',
    name: 'Users Data',
    title: 'Master Data Report',
    description: 'View complete user profiles including scan history, status, and location.',
    icon: faIdCard,
    endpoint: 'Users Data',
    category: 'Users',
    permissionKey: 'users-data-sheet'
  },
  {
    id: 'users-types',
    name: 'User Types',
    title: 'User Types Report',
    description: 'View user type classifications and distribution.',
    icon: faChartBar,
    endpoint: 'Users Types',
    category: 'Users',
    permissionKey: 'users-types-sheet'
  },
  {
    id: 'users-points',
    name: 'Users Points',
    title: 'User Points Report',
    description: 'Monitor user loyalty points and rewards.',
    icon: faGift,
    endpoint: 'Users Points',
    category: 'Users',
    permissionKey: 'users-points-sheet'
  },
  {
    id: 'referral-users',
    name: 'Referral Users',
    title: 'Referral Report',
    description: 'Track users who made referrals.',
    icon: faUserCheck,
    endpoint: 'Referral Users',
    category: 'Incentive',
    permissionKey: 'referral-users-sheet'
  },
  {
    id: 'referred-users',
    name: 'Referred Users',
    title: 'Referred Users Report',
    description: 'Track users who were referred by others.',
    icon: faHandshake,
    endpoint: 'Referred Users',
    category: 'Users',
    permissionKey: 'referred-users-sheet'
  },
  {
    id: 'national-ids',
    name: 'National IDs',
    title: 'National IDs Report',
    description: 'View national IDs uploaded by users for identity verification.',
    icon: faAddressCard,
    endpoint: 'National IDs',
    category: 'Users',
    permissionKey: 'national-ids-sheet'
  },
  // QR Codes Category
  {
    id: 'user-details-per-product',
    name: 'Product Scan Details',
    title: 'Scans',
    description: 'View detailed scan data by product and user action.',
    icon: faQrcode,
    endpoint: 'User Details Per Product Per Action',
    category: 'Incentive',
    permissionKey: 'users-details-per-product-per-Action-sheet'
  },
  {
    id: 'scans-per-user-type',
    name: 'Scans Per User Type',
    title: 'Scans Per User Type Report',
    description: 'Analyze scan patterns across different user types.',
    icon: faChartBar,
    endpoint: 'Scans per product per user type',
    category: 'Scans',
    permissionKey: 'scans-per-product-per-user-type-sheet'
  },
  {
    id: 'warehouse-scan',
    name: 'Warehouse Scans',
    title: 'Orders from Warehouse',
    description: 'Track warehouse inventory movements and scans.',
    icon: faBox,
    endpoint: 'Warehouse Scan In/Out',
    category: 'Warehouse',
    permissionKey: 'warehouse-scan-in-out-sheet'
  },
  {
    id: 'deactivated-qr',
    name: 'Deactivated QR',
    title: 'Deactivated Incentive QR',
    description: 'Monitor deactivated QR codes and incentives.',
    icon: faQrcode,
    endpoint: 'Deactivated Incentive QR',
    category: 'Scans',
    permissionKey: 'deactivated-incentive-qr-sheet'
  },
  {
    id: 'config-per-product',
    name: 'Configuration Results',
    title: 'Configuration Result Per Product',
    description: 'View configuration metrics per product.',
    icon: faClipboardList,
    endpoint: 'Configuration Result Per Product',
    category: 'Scans',
    permissionKey: 'configuration-result-per-product-sheet'
  },
  {
    id: 'order-scanout',
    name: 'Order Scanout',
    title: 'Order Scanout From Warehouse',
    description: 'Track orders scanned out from warehouse.',
    icon: faTruckFast,
    endpoint: 'Order Scanout From Warehouse',
    category: 'Warehouse',
    permissionKey: 'order-scanout-from-warehouse-sheet'
  },
  {
    id: 'warehouse-quantity',
    name: 'Warehouse Quantity',
    title: 'Warehouse Quantity Aggregation',
    description: 'Aggregate warehouse quantity data.',
    icon: faBox,
    endpoint: 'Warehouse Quantity Aggregation',
    category: 'Warehouse',
    permissionKey: 'warehouse-quantity-aggregation-sheet'
  },
  {
    id: 'scan-product-details',
    name: 'Scan Product Details',
    title: 'Scan Per Product Details',
    description: 'Detailed scan information per product.',
    icon: faQrcode,
    endpoint: 'Scan Per Product details',
    category: 'Scans',
    permissionKey: 'scan-per-product-details-sheet'
  },
  // Wallet Category
  {
    id: 'wallet-transactions',
    name: 'Wallet Transactions',
    title: 'Wallet Transactions',
    description: 'View all wallet transaction history.',
    icon: faWallet,
    endpoint: 'Wallet Transactions',
    category: 'Wallet',
    permissionKey: 'wallet-transactions-sheet'
  },
  {
    id: 'deliver-cash',
    name: 'Deliver Cash',
    title: 'Deliver Cash Report',
    description: 'Track cash delivery operations.',
    icon: faMoneyBill,
    endpoint: 'Deliver Cash',
    category: 'Wallet',
    permissionKey: 'deliver-cash-sheet'
  },
  {
    id: 'deliver-transfer',
    name: 'Deliver Transfer',
    title: 'Deliver Transfer Report',
    description: 'Track wallet transfer operations.',
    icon: faArrowsAlt,
    endpoint: 'Deliver Transfer',
    category: 'Wallet',
    permissionKey: 'deliver-transfer-sheet'
  },
  {
    id: 'welcome-bonus',
    name: 'Welcome Bonus',
    title: 'Welcome Bonus Report',
    description: 'Monitor welcome bonus distributions.',
    icon: faGift,
    endpoint: 'Welcome Bonus',
    category: 'Incentive',
    permissionKey: 'welcome-bonus-sheet'
  },
  // Payment Category
  {
    id: 'paymob',
    name: 'Smart Wallet Transactions',
    title: 'Smart Wallet Transactions',
    description: 'View Smart Wallet transactions and payment history.',
    icon: faCreditCard,
    endpoint: 'Paymob',
    category: 'Wallet',
    permissionKey: 'paymob-sheet'
  },
  // Invoices Category
  {
    id: 'invoice-photos',
    name: 'Invoice Photos',
    title: 'Invoice Photos Report',
    description: 'View invoice photo records.',
    icon: faImage,
    endpoint: 'invoice-photos',
    category: 'Invoices',
    permissionKey: 'invoice-photos-sheet'
  },
  // Logs Category
  {
    id: 'reset-logs',
    name: 'Reset Logs',
    title: 'Reset Logs Report',
    description: 'Track all system reset operations.',
    icon: faHistory,
    endpoint: 'reset-logs',
    category: 'Reset logs',
    permissionKey: 'reset-logs-sheet'
  },
  // Other Category
  {
    id: 'joining-requests',
    name: 'Joining Requests',
    title: 'Joining Requests Report',
    description: 'Monitor user joining requests.',
    icon: faUserCheck,
    endpoint: 'joining-requests',
    category: 'Users',
    permissionKey: 'joining-requests-sheet'
  },
  // Returns Category
  {
    id: 'return-system',
    name: 'Return System',
    title: 'Returns Report',
    description: 'Track product returns and returns data.',
    icon: faUndoAlt,
    endpoint: 'Return System',
    category: 'Warehouse',
    permissionKey: 'return-system-sheet'
  },
  {
    id: 'incentive-returns',
    name: 'Incentive Returns',
    title: 'Incentive Returns Report',
    description: 'Monitor incentive return operations.',
    icon: faGift,
    endpoint: 'Incentive',
    category: 'Warehouse',
    permissionKey: 'incentive-sheet'
  },
  // Deposits Category
  {
    id: 'deposit-amount',
    name: 'Deposit Amount',
    title: 'Deposit Amount Report',
    description: 'View deposit amount operations.',
    icon: faMoneyBill,
    endpoint: 'Deposit Amount',
    category: 'Deposits',
    permissionKey: 'deposit-amount-sheet'
  },
  {
    id: 'deposit-log',
    name: 'Deposit Log',
    title: 'Deposit Amount Log',
    description: 'View detailed deposit amount logs.',
    icon: faWallet,
    endpoint: 'Deposit Amount Log',
    category: 'Deposits',
    permissionKey: 'deposit-amount-log-sheet'
  },
  // Gifts Category
  {
    id: 'gifts',
    name: 'Gifts',
    title: 'Gifts Report',
    description: 'Track gift distribution and requests.',
    icon: faGift,
    endpoint: 'Gifts',
    category: 'Incentive',
    permissionKey: 'gifts-sheet'
  },
  {
    id: 'end-of-month',
    name: 'End Of Month',
    title: 'End Of Month Report',
    description: 'View end of month incentive summary including points earned and cash value.',
    icon: faCalendarCheck,
    endpoint: 'End Of Month',
    category: 'Incentive',
    permissionKey: 'end-of-month-sheet'
  }
];
